/**
 * WHAT THE FIRST VIEW MUST KEEP BELOW THE PHOTOGRAPH (the owner, 2026-11-03: "adaptive with limits ensuring the name and
 * the price and the bar shown").
 *
 * Publishes, while `active`:
 *  · `--pd-head-h` — the detail card from its top to the end of the price row (brand, name, price), followed with a
 *    ResizeObserver on the card: a name that wraps once the font arrives moves it;
 *  · `--pd-bar-h0` — the add-to-cart bar's height, measured once per phone width. In Instagram the bar grows by the
 *    home-indicator inset when the app's toolbar collapses on the first scroll; reading that would shrink the band
 *    under the shopper's thumb, which is the jump thePhotoStageIgnoresTheInAppToolbar.test.js refuses. A turn of the
 *    phone changes the width and measures it again.
 * The stylesheet turns them into the band (`--pd-hero-visible`, Pages/ProductDetail/styles.module.css).
 */
import { useEffect } from 'react';
import { publishRootLength, withdrawRootLength } from '../utils/rootLengths';

const HEAD = ['--pd-head-h', 'product-head'];
const BAR = ['--pd-bar-h0', 'product-cta-arrival'];

export default function useHeroHeadroom({ infoRef, priceRef, barRef, active = true }) {
  useEffect(() => {
    const info = infoRef.current;
    const price = priceRef.current;
    const bar = barRef.current;
    if (!active || !info || !price) {
      withdrawRootLength(...HEAD);
      withdrawRootLength(...BAR);
      return undefined;
    }
    const measureHead = () => {
      const head = price.getBoundingClientRect().bottom - info.getBoundingClientRect().top;
      publishRootLength(...HEAD, head > 0 ? head : 0);
    };
    const measureBar = () => {
      if (bar) publishRootLength(...BAR, bar.getBoundingClientRect().height);
    };
    measureHead();
    measureBar();
    let width = typeof window !== 'undefined' ? window.innerWidth : 0;
    const onResize = () => {
      if (window.innerWidth === width) return;
      width = window.innerWidth;
      measureBar();
      measureHead();
    };
    window.addEventListener('resize', onResize);
    let observer = null;
    if (typeof window.ResizeObserver === 'function') {
      observer = new window.ResizeObserver(measureHead);
      observer.observe(info);
    }
    return () => {
      window.removeEventListener('resize', onResize);
      if (observer) observer.disconnect();
      withdrawRootLength(...HEAD);
      withdrawRootLength(...BAR);
    };
  }, [infoRef, priceRef, barRef, active]);
}
